import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Loader2, Trash2 } from "lucide-react";

import { AddClientDialog } from "@/components/features/clients/AddClientDialog";
import {
  createEmptyRecurringLine,
  RecurringInvoiceLinesCard,
  type RecurringLineForm,
} from "@/components/features/invoices/RecurringInvoiceLinesCard";
import { DatePickerField } from "@/components/shared/DatePickerField";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  invoiceCreationBlockedEmptyExplanationKey,
  useInvoiceCreationBlocked,
} from "@/hooks/useInvoiceCreationBlocked";
import {
  parseBusinessDayOrdinal,
  settleBusinessDayOrdinal,
} from "@/pages/invoices/business-day-ordinal";
import { lineCurrencyFromClientDefault } from "@/lib/client-invoice-defaults";
import { CLIENT_SELECT_ADD_NEW_VALUE } from "@/lib/client-select-constants";
import { getLineTaxAmount } from "@/lib/invoice-line-tax";
import { deriveCfeTypeFromClientForRecurring } from "@/lib/recurring-invoice-cfe";
import { clientKeys } from "@/lib/queryKeys";
import { recurringInvoiceKeys } from "@/lib/queryKeys";
import { tenantKeys } from "@/lib/queryKeys";
import { getTenantCurrent } from "@/services/authService";
import { listClientsAllForSelection, type Client } from "@/services/clientService";
import {
  listClientCatalogItems,
  type ClientCatalogItem,
} from "@/services/clientCatalogService";
import {
  createRecurringSchedule,
  deleteRecurringSchedule,
  getRecurringSchedule,
  issueRecurringNow,
  updateRecurringSchedule,
  type MonthlyDateRule,
  type RecurringFrequency,
} from "@/services/recurringInvoiceService";
import { useAuth } from "@/contexts/AuthContext";
import { invoiceKeys } from "@/lib/queryKeys";
import { todayInUruguay, tomorrowInUruguay, parseLocalDateOnly } from "@/lib/dateFormat";
import type { TaxCategory } from "@fiscalo/shared-types";

function daysInMonth(year: number, month1to12: number): number {
  return new Date(year, month1to12, 0).getDate();
}

function isBusinessDay(year: number, month1to12: number, day: number): boolean {
  const dow = new Date(year, month1to12 - 1, day).getDay();
  return dow !== 0 && dow !== 6;
}

function firstBusinessDayOfMonth(year: number, month1to12: number): number {
  const last = daysInMonth(year, month1to12);
  for (let d = 1; d <= last; d += 1) {
    if (isBusinessDay(year, month1to12, d)) return d;
  }
  return 1;
}

function lastBusinessDayOfMonth(year: number, month1to12: number): number {
  for (let d = daysInMonth(year, month1to12); d > 0; d -= 1) {
    if (isBusinessDay(year, month1to12, d)) return d;
  }
  return 1;
}

function nthBusinessDayOfMonth(
  year: number,
  month1to12: number,
  n: number
): number {
  const last = daysInMonth(year, month1to12);
  let count = 0;
  for (let d = 1; d <= last; d += 1) {
    if (isBusinessDay(year, month1to12, d)) {
      count += 1;
      if (count === n) return d;
    }
  }
  return lastBusinessDayOfMonth(year, month1to12);
}

/** Count of business days from day 1 through `day` inclusive. */
function businessDayOrdinalOfDate(iso: string): number {
  const [yStr, mStr, dStr] = iso.split("-");
  const y = parseInt(yStr, 10);
  const m = parseInt(mStr, 10);
  const d = parseInt(dStr, 10);
  let count = 0;
  for (let day = 1; day <= d; day += 1) {
    if (isBusinessDay(y, m, day)) count += 1;
  }
  return count || 1;
}

function toIsoDateParts(year: number, month1to12: number, day: number): string {
  return `${year}-${String(month1to12).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * First scheduled run ≥ today, mirroring the backend's per-rule
 * normalize helpers. Holidays are not skipped (Mon–Fri only), same as
 * the server.
 */
function computeMonthlyNextSendDate(
  rule: MonthlyDateRule,
  businessDayOrdinal: number,
  todayIso: string
): string {
  const [yStr, mStr, dStr] = todayIso.split("-");
  const y = parseInt(yStr, 10);
  const m = parseInt(mStr, 10);
  const todayDay = parseInt(dStr, 10);
  const nextM = m === 12 ? 1 : m + 1;
  const nextY = m === 12 ? y + 1 : y;

  if (rule === "first_business_day_of_month") {
    const candidate = firstBusinessDayOfMonth(y, m);
    if (todayDay <= candidate) return toIsoDateParts(y, m, candidate);
    return toIsoDateParts(nextY, nextM, firstBusinessDayOfMonth(nextY, nextM));
  }
  if (rule === "last_business_day_of_month") {
    const candidate = lastBusinessDayOfMonth(y, m);
    if (todayDay <= candidate) return toIsoDateParts(y, m, candidate);
    return toIsoDateParts(nextY, nextM, lastBusinessDayOfMonth(nextY, nextM));
  }
  // nth_business_day (and legacy 'same_day' loaded into the form — the
  // load-effect maps it to nth_business_day before this fires)
  const candidate = nthBusinessDayOfMonth(y, m, businessDayOrdinal);
  if (todayDay <= candidate) return toIsoDateParts(y, m, candidate);
  return toIsoDateParts(
    nextY,
    nextM,
    nthBusinessDayOfMonth(nextY, nextM, businessDayOrdinal)
  );
}

function formatIsoDdMmYyyy(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export function RecurringInvoiceFormPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: tenant } = useQuery({
    queryKey: tenantKeys.current(user?.tenantId ?? undefined),
    queryFn: getTenantCurrent,
    enabled: Boolean(user?.tenantId),
  });

  const { blocked: invoicingBlocked, reason: invoicingBlockedReason } =
    useInvoiceCreationBlocked();

  useEffect(() => {
    if (isEdit || !tenant || !invoicingBlocked) return;
    navigate("/invoices/recurring", { replace: true });
    const key = invoiceCreationBlockedEmptyExplanationKey(invoicingBlockedReason);
    if (key) toast(t(key), { id: "invoicing-blocked" });
  }, [isEdit, tenant, invoicingBlocked, invoicingBlockedReason, navigate, t]);

  const { data: schedule, isLoading: loadingSchedule } = useQuery({
    queryKey: recurringInvoiceKeys.detail(id ?? ""),
    queryFn: () => getRecurringSchedule(id!),
    enabled: isEdit && Boolean(id),
  });

  const { data: clients = [], isLoading: clientsLoading } = useQuery({
    queryKey: clientKeys.listForSelect(),
    queryFn: listClientsAllForSelection,
  });

  const [clientId, setClientId] = useState("");
  const [currency, setCurrency] = useState<"UYU" | "USD">("UYU");
  const [linesForm, setLinesForm] = useState<RecurringLineForm[]>(() => [
    createEmptyRecurringLine(),
  ]);
  const [frequency, setFrequency] = useState<RecurringFrequency>("monthly");
  const [monthlyDateRule, setMonthlyDateRule] = useState<MonthlyDateRule>(
    "first_business_day_of_month"
  );
  // Two pieces of state on purpose: the committed ordinal drives the preview
  // and the payload, while the text is what the box shows and may be empty
  // mid-edit. Collapsing them back into one number is what made the field
  // impossible to clear.
  const [monthlyBusinessDayOrdinal, setMonthlyBusinessDayOrdinal] =
    useState<number>(1);
  const [monthlyBusinessDayOrdinalText, setMonthlyBusinessDayOrdinalText] =
    useState<string>("1");
  const commitBusinessDayOrdinal = useCallback((value: number) => {
    setMonthlyBusinessDayOrdinal(value);
    setMonthlyBusinessDayOrdinalText(String(value));
  }, []);
  const [onceIssueDate, setOnceIssueDate] = useState(() =>
    todayInUruguay()
  );
  const [isActive, setIsActive] = useState(true);
  const todayIso = useMemo(() => todayInUruguay(), []);
  const nextSendDate = useMemo(() => {
    if (frequency === "once") return onceIssueDate;
    return computeMonthlyNextSendDate(
      monthlyDateRule,
      monthlyBusinessDayOrdinal,
      todayIso
    );
  }, [
    frequency,
    onceIssueDate,
    monthlyDateRule,
    monthlyBusinessDayOrdinal,
    todayIso,
  ]);
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueNowDate, setIssueNowDate] = useState(() => todayInUruguay());
  const autoSingleCatalogKeyRef = useRef<string | null>(null);

  const { data: clientCatalogItems = [] } = useQuery({
    queryKey: clientKeys.catalog(clientId),
    queryFn: () => listClientCatalogItems(clientId),
    enabled: Boolean(clientId.trim()),
  });

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === clientId),
    [clients, clientId]
  );

  const cfeType = useMemo(
    () => deriveCfeTypeFromClientForRecurring(selectedClient),
    [selectedClient]
  );

  const applyClientCurrencyFromSelection = useCallback((client: Client) => {
    const derivedCfe = deriveCfeTypeFromClientForRecurring(client);
    const lineCur = lineCurrencyFromClientDefault(client, derivedCfe);
    if (lineCur) setCurrency(lineCur);
  }, []);

  useEffect(() => {
    if (cfeType === "121") {
      setCurrency("USD");
      setLinesForm((prev) =>
        prev.map((l) => ({
          ...l,
          taxCategory: "export" as TaxCategory,
          taxAmount: 0,
        }))
      );
    }
  }, [cfeType]);

  useEffect(() => {
    if (!schedule) return;
    setClientId(schedule.clientId);
    setCurrency(schedule.currency);
    if (schedule.lines?.length) {
      setLinesForm(
        schedule.lines.map((l) => {
          const taxCategory = l.taxCategory as TaxCategory;
          return {
            description: l.description,
            quantity: l.quantity,
            unitPrice: l.unitPrice,
            taxCategory,
            taxAmount: getLineTaxAmount(
              taxCategory,
              l.quantity,
              l.unitPrice
            ),
          };
        })
      );
    } else {
      const taxCategory = schedule.taxCategory as TaxCategory;
      setLinesForm([
        {
          description: schedule.lineDescription,
          quantity: 1,
          unitPrice: schedule.amount,
          taxCategory,
          taxAmount: getLineTaxAmount(taxCategory, 1, schedule.amount),
        },
      ]);
    }
    setFrequency(schedule.frequency);
    // Migrate legacy `same_day` schedules to `nth_business_day` in the
    // form (derive the ordinal from the persisted nextSendDate). The
    // schedule isn't rewritten until the user saves — until then the
    // backend keeps using the original rule. This silent map keeps the
    // calendar-day option out of the simplified UI while still letting
    // pre-existing schedules be edited safely.
    const storedRule = schedule.monthlyDateRule ?? "same_day";
    if (storedRule === "same_day") {
      setMonthlyDateRule("nth_business_day");
      commitBusinessDayOrdinal(
        businessDayOrdinalOfDate(schedule.nextSendDate)
      );
    } else {
      setMonthlyDateRule(storedRule);
      if (storedRule === "nth_business_day") {
        commitBusinessDayOrdinal(
          schedule.monthlyBusinessDayOrdinal ??
            businessDayOrdinalOfDate(schedule.nextSendDate)
        );
      }
    }
    if (schedule.frequency === "once") setOnceIssueDate(schedule.nextSendDate);
    setIsActive(schedule.isActive);
    // commitBusinessDayOrdinal is useCallback([]) and so stable — listing it
    // satisfies exhaustive-deps without re-running this hydration effect.
  }, [schedule, commitBusinessDayOrdinal]);

  const updateLine = useCallback(
    (index: number, patch: Partial<RecurringLineForm>) => {
      setLinesForm((prev) =>
        prev.map((line, i) => {
          if (i !== index) return line;
          const updated = { ...line, ...patch };
          const needsTaxRecalc =
            "quantity" in patch ||
            "unitPrice" in patch ||
            "taxCategory" in patch;
          if (needsTaxRecalc) {
            updated.taxAmount = getLineTaxAmount(
              updated.taxCategory,
              updated.quantity,
              updated.unitPrice
            );
          }
          return updated;
        })
      );
    },
    []
  );

  const applySingleCatalogToFirstLine = useCallback(
    (item: ClientCatalogItem) => {
      const tax: TaxCategory =
        cfeType === "121"
          ? "export"
          : ((item.defaultTaxCategory as TaxCategory) || "vat_basic");
      updateLine(0, {
        description: item.description,
        unitPrice: item.unitPrice,
        taxCategory: tax,
        catalogItemId: item.id,
      });
    },
    [cfeType, updateLine]
  );

  useEffect(() => {
    if (isEdit) return;
    if (!clientId.trim()) {
      autoSingleCatalogKeyRef.current = null;
      return;
    }
    if (clientCatalogItems.length !== 1) {
      autoSingleCatalogKeyRef.current = null;
      return;
    }
    const item = clientCatalogItems[0];
    const key = `${clientId}:${item.id}`;
    if (autoSingleCatalogKeyRef.current === key) return;
    autoSingleCatalogKeyRef.current = key;
    applySingleCatalogToFirstLine(item);
  }, [isEdit, clientId, clientCatalogItems, applySingleCatalogToFirstLine]);

  const addLine = useCallback(() => {
    const tax: TaxCategory = cfeType === "121" ? "export" : "vat_basic";
    const empty = createEmptyRecurringLine(tax);
    if (cfeType === "121") {
      empty.taxAmount = 0;
    }
    setLinesForm((prev) => [...prev, empty]);
  }, [cfeType]);

  const removeLine = useCallback((index: number) => {
    setLinesForm((prev) =>
      prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)
    );
  }, []);

  const linesPayload = useMemo(
    () =>
      linesForm.map((l) => ({
        description: l.description.trim(),
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        taxCategory: l.taxCategory,
      })),
    [linesForm]
  );

  const createMut = useMutation({
    mutationFn: () =>
      createRecurringSchedule({
        clientId,
        currency,
        lines: linesPayload,
        frequency,
        ...(frequency === "monthly"
          ? {
              monthlyDateRule,
              ...(monthlyDateRule === "nth_business_day"
                ? { monthlyBusinessDayOrdinal }
                : {}),
            }
          : {}),
        nextSendDate,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: recurringInvoiceKeys.all });
      toast.success(t("recurringInvoices.toast.saved"));
      navigate("/invoices/recurring");
    },
    onError: () => {
      toast.error(t("recurringInvoices.toast.saveError"));
    },
  });

  const updateMut = useMutation({
    mutationFn: () =>
      updateRecurringSchedule(id!, {
        clientId,
        currency,
        lines: linesPayload,
        frequency,
        ...(frequency === "monthly"
          ? {
              monthlyDateRule,
              ...(monthlyDateRule === "nth_business_day"
                ? { monthlyBusinessDayOrdinal }
                : {}),
            }
          : {}),
        nextSendDate,
        isActive,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: recurringInvoiceKeys.all });
      toast.success(t("recurringInvoices.toast.saved"));
      navigate("/invoices/recurring");
    },
    onError: () => {
      toast.error(t("recurringInvoices.toast.saveError"));
    },
  });

  const issueNowMut = useMutation({
    mutationFn: async (): Promise<{ invoiceId: string }> => {
      const created = await createRecurringSchedule({
        clientId,
        currency,
        lines: linesPayload,
        frequency,
        ...(frequency === "monthly"
          ? {
              monthlyDateRule,
              ...(monthlyDateRule === "nth_business_day"
                ? { monthlyBusinessDayOrdinal }
                : {}),
            }
          : {}),
        nextSendDate,
      });
      return issueRecurringNow(created.id, issueNowDate);
    },
    onSuccess: async ({ invoiceId }) => {
      setIssueDialogOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: recurringInvoiceKeys.all }),
        queryClient.invalidateQueries({ queryKey: invoiceKeys.all }),
      ]);
      toast.success(t("recurringInvoices.toast.issuedNow"));
      navigate(`/invoices/${invoiceId}`);
    },
    onError: () => {
      toast.error(t("recurringInvoices.toast.issueNowError"));
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteRecurringSchedule(id!),
    onSuccess: async () => {
      setDeleteDialogOpen(false);
      await queryClient.invalidateQueries({ queryKey: recurringInvoiceKeys.all });
      toast.success(t("recurringInvoices.toast.deleted"));
      navigate("/invoices/recurring");
    },
    onError: () => {
      toast.error(t("recurringInvoices.toast.deleteError"));
    },
  });

  const submitting =
    createMut.isPending || updateMut.isPending || issueNowMut.isPending;

  const canSubmit = useMemo(() => {
    const linesValid = linesForm.every(
      (l) =>
        l.description.trim().length > 0 &&
        l.quantity > 0 &&
        l.unitPrice >= 0
    );
    const scheduleValid =
      frequency === "once"
        ? /^\d{4}-\d{2}-\d{2}$/.test(onceIssueDate) && onceIssueDate >= todayIso
        : monthlyDateRule === "nth_business_day"
          ? Number.isInteger(monthlyBusinessDayOrdinal) &&
            monthlyBusinessDayOrdinal >= 1 &&
            monthlyBusinessDayOrdinal <= 23
          : true;
    return Boolean(clientId.trim()) && linesValid && scheduleValid;
  }, [
    clientId,
    linesForm,
    frequency,
    onceIssueDate,
    monthlyDateRule,
    monthlyBusinessDayOrdinal,
    todayIso,
  ]);

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    if (isEdit) updateMut.mutate();
    else createMut.mutate();
  };

  const handleConfirmDelete = (): void => {
    if (id) deleteMut.mutate();
  };

  if (isEdit && loadingSchedule) {
    return <LoadingPage />;
  }

  if (!isEdit && tenant && invoicingBlocked) {
    return <LoadingPage />;
  }

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={
          isEdit
            ? t("recurringInvoices.form.titleEdit")
            : t("recurringInvoices.form.titleNew")
        }
        description={t("recurringInvoices.form.subtitle")}
        onBack={() => navigate("/invoices/recurring")}
        actions={
          isEdit ? (
            <Button
              type="button"
              variant="destructive"
              className="transition-colors duration-150"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t("recurringInvoices.form.delete")}
            </Button>
          ) : undefined
        }
      />

      <form onSubmit={onSubmit} className="space-y-4">
        {/* Receptor — same card as new invoice */}
        <Card>
          <CardHeader>
            <CardTitle>
              {t("invoices.new.receiver")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label
                htmlFor="recurring-client"
                className="text-sm font-semibold text-foreground"
              >
                {t("invoices.create.selectClient", "Select a client")}
              </Label>
              <Select
                value={clientId || "none"}
                onValueChange={(v) => {
                  if (v === CLIENT_SELECT_ADD_NEW_VALUE) {
                    setAddClientOpen(true);
                    return;
                  }
                  if (v === "none") {
                    setClientId("");
                    setCurrency("UYU");
                    return;
                  }
                  setClientId(v);
                  const picked = clients.find((c) => c.id === v);
                  if (picked) applyClientCurrencyFromSelection(picked);
                }}
                disabled={clientsLoading}
              >
                <SelectTrigger id="recurring-client" className="h-10 transition-colors duration-150">
                  <SelectValue
                    placeholder={
                      clientsLoading
                        ? t("invoices.create.loadingClients", "Loading…")
                        : t("invoices.create.selectClientPlaceholder", "Optional – choose to pre-fill")
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">
                    {t("invoices.create.noClient", "None")}
                  </SelectItem>
                  {clients.map((c: Client) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.displayName?.trim() || c.legalName}
                    </SelectItem>
                  ))}
                  <SelectItem
                    value={CLIENT_SELECT_ADD_NEW_VALUE}
                    className="cursor-pointer text-primary focus:text-primary"
                  >
                    {t("invoices.create.addNewClient")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tipo de factura / client snapshot card removed in the recurring flow —
            client choice already implies cfeType, country, doc and payment is
            always contado, so this card was purely echoing client data. */}

        {/* Recurring-only: schedule */}
        {selectedClient ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-foreground">
                {t("recurringInvoices.form.scheduleCardTitle")}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {t("recurringInvoices.form.scheduleCardSubtitle")}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t("recurringInvoices.form.frequency")}</Label>
                <Select
                  value={frequency}
                  onValueChange={(v) => {
                    const next = v as RecurringFrequency;
                    setFrequency(next);
                    if (next === "once") {
                      setMonthlyDateRule("same_day");
                    }
                  }}
                >
                  <SelectTrigger className="transition-colors duration-150">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">
                      {t("recurringInvoices.frequency.monthly")}
                    </SelectItem>
                    <SelectItem value="once">
                      {t("recurringInvoices.frequency.once")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {frequency === "monthly" ? (
                <>
                  <div className="space-y-2">
                    <Label>{t("recurringInvoices.form.monthlyDateRule")}</Label>
                    <Select
                      value={
                        // Legacy `same_day` is shown as Personalizado in
                        // the UI — same as how the load-effect mapped it.
                        monthlyDateRule === "same_day"
                          ? "nth_business_day"
                          : monthlyDateRule
                      }
                      onValueChange={(v) =>
                        setMonthlyDateRule(v as MonthlyDateRule)
                      }
                    >
                      <SelectTrigger className="transition-colors duration-150">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="first_business_day_of_month">
                          {t(
                            "recurringInvoices.monthlyDateRule.firstBusinessDay"
                          )}
                        </SelectItem>
                        <SelectItem value="last_business_day_of_month">
                          {t(
                            "recurringInvoices.monthlyDateRule.lastBusinessDay"
                          )}
                        </SelectItem>
                        <SelectItem value="nth_business_day">
                          {t("recurringInvoices.monthlyDateRule.custom")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {monthlyDateRule === "nth_business_day" ? (
                    <div className="space-y-2">
                      <Label htmlFor="recurring-business-day-ordinal">
                        {t("recurringInvoices.form.businessDayOrdinal")}
                      </Label>
                      <Input
                        id="recurring-business-day-ordinal"
                        type="number"
                        min={1}
                        max={23}
                        step={1}
                        value={monthlyBusinessDayOrdinalText}
                        onChange={(e) => {
                          // Show whatever was typed — including nothing, so
                          // the field can actually be cleared and retyped.
                          const raw = e.target.value;
                          setMonthlyBusinessDayOrdinalText(raw);
                          const parsed = parseBusinessDayOrdinal(raw);
                          if (parsed != null) {
                            setMonthlyBusinessDayOrdinal(parsed);
                          }
                        }}
                        onBlur={() =>
                          commitBusinessDayOrdinal(
                            settleBusinessDayOrdinal(
                              monthlyBusinessDayOrdinalText,
                              monthlyBusinessDayOrdinal
                            )
                          )
                        }
                        className="w-32"
                      />
                      <p className="text-sm text-muted-foreground">
                        {t("recurringInvoices.form.businessDayOrdinalHint")}
                      </p>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="space-y-2">
                  <DatePickerField
                    id="recurring-once-date"
                    label={t("recurringInvoices.form.onceIssueDate")}
                    value={onceIssueDate}
                    onChange={setOnceIssueDate}
                  />
                </div>
              )}

              <div className="rounded-md border border-[var(--fx-ink-100)] bg-[rgba(22,21,20,0.04)] px-3 py-2 text-sm text-foreground dark:border-[var(--dk-border)] dark:bg-[rgba(244,239,230,0.06)]">
                <span className="font-medium">
                  {t("recurringInvoices.form.nextRunLabel")}{" "}
                </span>
                <span>{formatIsoDdMmYyyy(nextSendDate)}</span>
              </div>

              {isEdit ? (
                <div className="flex items-start gap-3 rounded-lg border p-4">
                  <Checkbox
                    id="recurring-active"
                    checked={isActive}
                    onCheckedChange={(checked) => setIsActive(checked === true)}
                    className="mt-1"
                  />
                  <div className="grid gap-1.5 leading-none">
                    <Label
                      htmlFor="recurring-active"
                      className="cursor-pointer font-medium text-foreground leading-none"
                    >
                      {t("recurringInvoices.form.activeLabel")}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {t("recurringInvoices.form.activeHint")}
                    </p>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        ) : null}

        {selectedClient ? (
          <RecurringInvoiceLinesCard
            linesForm={linesForm}
            updateLine={updateLine}
            addLine={addLine}
            removeLine={removeLine}
            cfeType={cfeType}
            currency={currency}
            onCurrencyChange={setCurrency}
            clientCatalogItems={clientCatalogItems}
            tenantExoneratedRegimeEnabled={
              tenant?.exoneratedRegimeEnabled === true ? true : undefined
            }
            tenantExoneratedRegimeLegend={tenant?.exoneratedRegimeLegend}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-foreground">
                {t("invoices.new.lines")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {t("recurringInvoices.form.linesAfterClient")}
              </p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="flex flex-col gap-2 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <Button
              type="button"
              variant="outline"
              className="transition-colors duration-150"
              onClick={() => navigate("/invoices/recurring")}
            >
              {t("recurringInvoices.form.cancel")}
            </Button>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {!isEdit && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={!canSubmit || submitting}
                  className="min-w-[160px] transition-colors duration-150"
                  onClick={() => {
                    setIssueNowDate(todayInUruguay());
                    setIssueDialogOpen(true);
                  }}
                >
                  {t("recurringInvoices.form.saveAndIssue")}
                </Button>
              )}
              <Button type="submit" disabled={!canSubmit || submitting} className="min-w-[120px]">
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("recurringInvoices.form.saving")}
                  </>
                ) : isEdit ? (
                  t("recurringInvoices.form.save")
                ) : (
                  t("recurringInvoices.form.create")
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={t("recurringInvoices.form.deleteConfirmTitle")}
        description={t("recurringInvoices.form.deleteConfirmDescription")}
        confirmText={t("recurringInvoices.form.deleteConfirm")}
        cancelText={t("recurringInvoices.form.cancel")}
        loadingText={t("recurringInvoices.form.deleting")}
        variant="destructive"
        isLoading={deleteMut.isPending}
        onConfirm={handleConfirmDelete}
      />

      <AddClientDialog
        open={addClientOpen}
        onOpenChange={setAddClientOpen}
        onCreated={(client) => {
          setClientId(client.id);
          applyClientCurrencyFromSelection(client);
        }}
      />

      <Dialog
        open={issueDialogOpen}
        onOpenChange={(open) => {
          if (issueNowMut.isPending) return;
          setIssueDialogOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("recurringInvoices.issueNow.title")}
            </DialogTitle>
            <DialogDescription>
              {t("recurringInvoices.issueNow.intro")}
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 pl-0 text-sm text-foreground">
            <li className="flex gap-3">
              <span
                className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--fx-ink-900)] text-xs font-semibold text-white dark:bg-[var(--fx-paper)] dark:text-[var(--fx-ink-900)]"
                aria-hidden
              >
                1
              </span>
              <span>{t("recurringInvoices.issueNow.step1")}</span>
            </li>
            <li className="flex gap-3">
              <span
                className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--fx-ink-900)] text-xs font-semibold text-white dark:bg-[var(--fx-paper)] dark:text-[var(--fx-ink-900)]"
                aria-hidden
              >
                2
              </span>
              <span>{t("recurringInvoices.issueNow.step2")}</span>
            </li>
          </ol>
          <div className="space-y-2 py-2">
            <DatePickerField
              id="recurring-issue-now-date"
              label={t("recurringInvoices.issueNow.dateLabel")}
              value={issueNowDate}
              onChange={setIssueNowDate}
              maxDate={parseLocalDateOnly(tomorrowInUruguay())}
            />
            <p className="text-sm text-muted-foreground">
              {t("recurringInvoices.issueNow.dateHint")}
            </p>
          </div>
          <p className="rounded-md border border-[rgba(201,94,62,0.30)] bg-[rgba(201,94,62,0.08)] px-3 py-2 text-sm text-[var(--fx-ink-900)] dark:border-[rgba(201,94,62,0.45)] dark:bg-[rgba(201,94,62,0.12)] dark:text-[var(--dk-text)]">
            {t("recurringInvoices.issueNow.warning")}
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={issueNowMut.isPending}
              onClick={() => setIssueDialogOpen(false)}
            >
              {t("recurringInvoices.form.cancel")}
            </Button>
            <Button
              type="button"
              disabled={
                !canSubmit ||
                issueNowMut.isPending ||
                !/^\d{4}-\d{2}-\d{2}$/.test(issueNowDate)
              }
              onClick={() => issueNowMut.mutate()}
              className="min-w-[140px]"
            >
              {issueNowMut.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("recurringInvoices.issueNow.issuing")}
                </>
              ) : (
                t("recurringInvoices.issueNow.confirm")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
